import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, MessageCircle, Mail, MapPin, Check } from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import usePageMeta from "@/hooks/usePageMeta";
import FAQ from "@/components/ui/FAQ";
import OrderFlow from "@/components/ui/OrderFlow";
import { wa } from "@/constants/content";
import { API_URL } from "@/lib/api";
import {
  CONTACT_SERVICE_COUNT,
  CONTACT_SERVICE_LOOKUP,
  CONTACT_SERVICE_OPTION_GROUPS,
  DEFAULT_CONTACT_SERVICE_CODE,
  DEFAULT_CONTACT_SERVICE_LABEL,
} from "@/constants/serviceCatalog";

export default function Contact() {
  usePageMeta({
    title: "Contact · Start a brief",
    description:
      "Need a cleaner quote flow for the full 2026 ThemeStudios catalog? Grouped service selection, mobile-first enquiry, and a direct WhatsApp route. Madurai, Tamil Nadu.",
    path: "/contact",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: DEFAULT_CONTACT_SERVICE_CODE,
    brief: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, err: "" });

  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setState({ loading: true, ok: false, err: "" });
    try {
      await axios.post(`${API_URL}/contact`, {
        ...form,
        service_code: form.service,
        service:
          CONTACT_SERVICE_LOOKUP[form.service]?.label ||
          DEFAULT_CONTACT_SERVICE_LABEL,
      });
      setState({ loading: false, ok: true, err: "" });
      setForm({
        name: "",
        email: "",
        phone: "",
        service: DEFAULT_CONTACT_SERVICE_CODE,
        brief: "",
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not send. Try WhatsApp instead.";
      setState({ loading: false, ok: false, err: String(message) });
    }
  };

  return (
    <div data-testid="page-contact" className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
          ✱ Dedicated contact page
        </p>
        <h1 className="h1-display text-white text-[8vw] md:text-[4.5vw] max-w-[22ch]">
          Choose from the grouped{" "}
          <span className="text-[#e03c31]">2026 catalog</span> or ask for a
          recommendation.
        </h1>
        <p className="mt-6 max-w-2xl text-sm md:text-[15px] text-zinc-300 leading-relaxed">
          Use the form for a fast first enquiry, or jump straight to WhatsApp
          for a real-time thread. Either route lands in the same studio queue
          across posters, reels, branding, ads support, and monthly systems.
        </p>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-16 grid grid-cols-1 md:grid-cols-12 gap-8">
        <form
          onSubmit={submit}
          className="md:col-span-7 glass rounded-3xl p-8 md:p-10"
          data-testid="contact-form"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-3">
            ✱ Full catalog enquiry
          </p>
          <h2 className="font-cabinet text-2xl md:text-3xl font-bold mb-2">
            Send the brief
          </h2>
          <p className="text-sm text-zinc-400 mb-7">
            Mobile number stays mandatory for a reliable callback. Choose the
            closest service only if you already know it.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                Your name *
              </label>
              <input
                data-testid={CONTACT.name}
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Full name"
                className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e03c31]/60 transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                Mobile number *
              </label>
              <input
                data-testid={CONTACT.phone}
                required
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e03c31]/60 transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                Email *
              </label>
              <input
                data-testid={CONTACT.email}
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@yourbrand.com"
                className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e03c31]/60 transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                Service or package (optional)
              </label>
              <select
                data-testid={CONTACT.service}
                value={form.service}
                onChange={update("service")}
                className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e03c31]/60 transition-colors"
              >
                {CONTACT_SERVICE_OPTION_GROUPS.map((group) => (
                  <optgroup
                    key={group.group}
                    label={group.group}
                    className="bg-[#0a0a0c]"
                  >
                    {group.options.map((option) => (
                      <option
                        key={option.code}
                        value={option.code}
                        className="bg-[#0a0a0c]"
                      >
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                Your brief *
              </label>
              <textarea
                data-testid={CONTACT.brief}
                required
                rows={6}
                value={form.brief}
                onChange={update("brief")}
                placeholder="Business name, offer, language (Tamil / English / both), platform (WhatsApp / Instagram / Facebook), preferred style, deadline..."
                className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e03c31]/60 transition-colors resize-none"
              />
            </div>
          </div>

          <div className="mt-7 flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-zinc-500 max-w-sm leading-relaxed">
              {CONTACT_SERVICE_COUNT} active services and packs across posters,
              reels, branding, ads support, and monthly systems.
            </p>
            <button
              data-testid={CONTACT.submit}
              type="submit"
              disabled={state.loading}
              className="btn-magnetic disabled:opacity-50"
            >
              {state.loading ? "Sending..." : "Send enquiry"}{" "}
              <ArrowUpRight size={16} />
            </button>
          </div>

          {state.ok && (
            <div
              data-testid={CONTACT.success}
              className="mt-5 flex items-center gap-2 text-emerald-400 text-sm bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3"
            >
              <Check size={16} /> Got it. We&apos;ll reach out shortly on
              WhatsApp or email.
            </div>
          )}
          {state.err && (
            <div
              data-testid={CONTACT.error}
              className="mt-5 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
            >
              {state.err}
            </div>
          )}
        </form>

        <aside className="md:col-span-5 space-y-4">
          <a
            href={wa("brief")}
            target="_blank"
            rel="noreferrer"
            data-testid={CONTACT.whatsapp}
            className="block group bg-[#0a0a0c] border border-white/5 hover:border-[#e03c31]/40 rounded-3xl p-7 transition-colors"
          >
            <div className="flex items-start justify-between mb-6">
              <MessageCircle size={22} className="text-[#e03c31]" />
              <ArrowUpRight
                size={18}
                className="text-zinc-500 group-hover:text-white transition-colors"
              />
            </div>
            <h3 className="font-cabinet text-2xl font-bold mb-1.5">
              Talk on WhatsApp
            </h3>
            <p className="text-zinc-400 text-sm mb-4">
              Fastest route. The prefilled template asks for business name,
              logo, offer text, language, platform, and delivery time so the
              brief lands ready.
            </p>
            <p className="text-white text-sm">+91 8903 205 676</p>
          </a>

          <a
            href="mailto:support@themestudios.in"
            data-testid={CONTACT.emailCta}
            className="block group bg-[#0a0a0c] border border-white/5 hover:border-[#e03c31]/40 rounded-3xl p-7 transition-colors"
          >
            <div className="flex items-start justify-between mb-6">
              <Mail size={22} className="text-[#e03c31]" />
              <ArrowUpRight
                size={18}
                className="text-zinc-500 group-hover:text-white transition-colors"
              />
            </div>
            <h3 className="font-cabinet text-2xl font-bold mb-1.5">
              Email the studio
            </h3>
            <p className="text-zinc-400 text-sm mb-4">
              For larger briefs, decks, or RFPs.
            </p>
            <p className="text-white text-sm">support@themestudios.in</p>
          </a>

          <div className="bg-[#0a0a0c] border border-white/5 rounded-3xl p-7">
            <div className="flex items-start justify-between mb-6">
              <MapPin size={22} className="text-[#e03c31]" />
            </div>
            <h3 className="font-cabinet text-2xl font-bold mb-1.5">
              Merchant identity
            </h3>
            <p className="font-bold text-white text-sm mb-1">
              ThemeStudios Digital Designers
            </p>
            <p className="text-zinc-400 text-sm">
              Remote design studio based in Madurai, serving small businesses
              across Tamil Nadu and beyond.
            </p>
          </div>

          <div className="bg-[#0a0a0c] border border-white/5 rounded-3xl p-7">
            <h3 className="font-cabinet text-xl font-bold mb-4">
              Before you submit
            </h3>
            <ul className="space-y-2.5 text-sm text-zinc-300">
              <li className="flex items-start gap-2">
                <Check size={14} className="text-[#e03c31] mt-1 shrink-0" />
                Choose the closest service only if you already know it.
              </li>
              <li className="flex items-start gap-2">
                <Check size={14} className="text-[#e03c31] mt-1 shrink-0" />
                Mobile number stays mandatory for a reliable callback.
              </li>
              <li className="flex items-start gap-2">
                <Check size={14} className="text-[#e03c31] mt-1 shrink-0" />
                Ads setup and ads management pricing do not include ad spend.
              </li>
            </ul>
          </div>
        </aside>
      </section>

      <OrderFlow />
      <FAQ />
    </div>
  );
}
